import { InjectionToken, Provider } from '@angular/core';
import { environment } from '@env/environment';
import { LEGAL_DOCS, LegalDoc } from './legal.content';

/**
 * Centro legal del storefront (cards #472/#473, provider #484).
 *
 * TODO lo legal del storefront —footer, páginas de T&C/privacidad/devoluciones,
 * botón de arrepentimiento— lee de UN solo objeto {@link StorefrontLegalConfig},
 * nunca de literales desparramados en templates. La identidad legal es dato
 * PER-ORG, no del código: hoy se sirve el placeholder ({@link PLACEHOLDER_LEGAL_CONFIG}),
 * y cuando el backend exponga la identidad legal per-org por el endpoint de
 * branding (**card #483**, feature vendible) el swap es cambiar el provider por
 * uno que arme el MISMO shape desde el endpoint — 1:1, sin tocar componentes ni
 * contenido. Ese es el punto de integración: {@link provideStorefrontLegal}.
 */

/** Identidad legal de la empresa (dato per-org). Los textos autocompletan de acá. */
export interface LegalIdentity {
  /** Nombre visible del sitio. */
  readonly siteName: string;
  /** Dominio público (ej. `tienda.example.com`). */
  readonly domain: string;
  /** Razón social. */
  readonly businessName: string;
  /** CUIT. */
  readonly taxId: string;
  /** Domicilio legal/comercial completo (localidad y provincia). */
  readonly address: string;
  /** Email de contacto general. */
  readonly email: string;
  /** Email del responsable de datos personales (Ley 25.326). */
  readonly privacyEmail: string;
  /** Teléfono / WhatsApp. */
  readonly phone: string;
  /** Horario de atención. */
  readonly hours: string;
  /** Área responsable del servicio de atención al cliente. */
  readonly supportArea: string;
}

/** El centro legal completo del storefront. */
export interface StorefrontLegalConfig {
  /**
   * Modo legal, con dos comportamientos:
   * - `demo`: el circuito legal está VISIBLE con los valores genéricos + un banner
   *   "Demostración" (textos de ejemplo, sin valor legal), para poder probar todo.
   *   Es lo que corre en la tienda demo de prod (productos ficticios, sin venta).
   * - `real`: el default del producto. El circuito legal queda OCULTO hasta que el
   *   operador cargue sus datos reales y ponga {@link legalReady} en `true` — así
   *   ningún deploy real publica datos genéricos/falsos.
   *
   * El circuito (footer, páginas, arrepentimiento) es IDÉNTICO en ambos modos; lo
   * único que cambia es la visibilidad y el banner. Ver {@link isLegalVisible}.
   */
  readonly mode: 'demo' | 'real';
  /**
   * Interruptor del operador para el modo `real`: mientras sea `false`, lo legal
   * no se linkea ni se expone (DARK). En modo `demo` no aplica (siempre visible).
   */
  readonly legalReady: boolean;
  readonly identity: LegalIdentity;
  /** Data Fiscal 960/D de ARCA: script/HTML oficial (interactivo) o URL declarada. */
  readonly dataFiscal: { readonly embed: string };
  /** Los tres documentos legales; autocompletan sus tokens desde `identity`. */
  readonly texts: {
    readonly terminos: LegalDoc;
    readonly privacidad: LegalDoc;
    readonly devoluciones: LegalDoc;
  };
  /** Circuito del botón de arrepentimiento (Disp. 954/2025). */
  readonly arrepentimiento: {
    /** Email destino del trámite de 24 h (canal manual hasta #329). */
    readonly emailDestino: string;
  };
  /** Libro de quejas on-line (Ley CABA 2247) — opcional; `null` = no aplica. */
  readonly libroQuejas: { readonly url: string | null };
}

/**
 * Reemplaza los tokens `{campo}` de un texto con los valores de {@link LegalIdentity}.
 * Un token desconocido se deja intacto (defensivo). Es el "autocompletado desde
 * identidad": el contenido legal referencia `{businessName}`/`{taxId}`/… y acá se
 * resuelven a un único origen. Si el valor sigue siendo un placeholder `[…]`, el
 * resaltado de la página lo marca igual.
 * @param text Texto con tokens `{campo}`.
 * @param identity Identidad legal vigente.
 * @returns El texto con los tokens resueltos.
 */
export function fillIdentityTokens(text: string, identity: LegalIdentity): string {
  const fields = identity as unknown as Record<string, string>;
  return text.replace(/\{(\w+)\}/g, (match, key: string) => (key in fields ? String(fields[key]) : match));
}

/**
 * Token DI del centro legal. Lo proveen {@link provideStorefrontLegal} (placeholder)
 * o, en el futuro, un provider que lo arme desde el endpoint de branding (#483).
 */
export const STOREFRONT_LEGAL_CONFIG = new InjectionToken<StorefrontLegalConfig>(
  'STOREFRONT_LEGAL_CONFIG',
);

/**
 * Si el circuito legal debe MOSTRARSE: siempre en `demo`, y en `real` sólo cuando
 * el operador declaró `legalReady`. Único punto de verdad de la visibilidad, que
 * usan el guard de rutas y el footer.
 * @param config Centro legal.
 * @returns `true` si el footer y las páginas legales deben exponerse.
 */
export function isLegalVisible(config: StorefrontLegalConfig): boolean {
  return config.mode === 'demo' || config.legalReady;
}

/**
 * Si hay que mostrar el banner "Demostración" (valores de ejemplo, sin valor
 * legal): sólo en modo `demo`.
 * @param config Centro legal.
 * @returns `true` en modo demo.
 */
export function isLegalDemo(config: StorefrontLegalConfig): boolean {
  return config.mode === 'demo';
}

/**
 * Identidad legal por DEFAULT: valores genéricos y presentables, NO datos reales
 * de nadie. El código nunca conoce la identidad real de un operador — cada tienda
 * la carga (ver README.md de esta carpeta). Es un ÚNICO origen: al editarla, los
 * valores fluyen a footer, páginas y arrepentimiento. Con `legalReady=false` nada
 * de esto se muestra, así que estos genéricos no llegan al público.
 */
const DEFAULT_IDENTITY: LegalIdentity = {
  siteName: 'Tu Tienda',
  domain: 'tutienda.com',
  businessName: 'Razón Social S.A.',
  taxId: '30-00000000-0',
  address: 'Calle Ejemplo 123, Ciudad, Provincia',
  email: 'contacto@tutienda.com',
  privacyEmail: 'privacidad@tutienda.com',
  phone: '+54 11 0000-0000',
  hours: 'Lunes a viernes de 9 a 18 h',
  supportArea: 'Atención al Cliente',
};

/**
 * Centro legal por DEFAULT: **fail-closed**. `mode:'real'` + `legalReady:false`
 * ⇒ el circuito legal queda en DARK (ni el footer ni las rutas legales se
 * exponen), así ningún build publica identidad genérica sin que alguien lo pida
 * explícitamente. Trae la identidad genérica, los textos (que autocompletan sus
 * tokens desde esa identidad), el Data Fiscal vacío, el email del arrepentimiento
 * genérico y el libro de quejas apagado — todo inerte hasta que el operador cargue
 * sus datos reales y prenda `legalReady`, o #483 reemplace el provider 1:1 desde
 * el endpoint de branding per-org. El modo `demo` (visible + banner) es un opt-in
 * explícito por entorno, ver {@link provideStorefrontLegal}.
 */
export const DEFAULT_LEGAL_CONFIG: StorefrontLegalConfig = {
  // Fail-closed: real + legalReady:false ⇒ DARK. El modo demo se pide por entorno
  // (environment.legalDemo), nunca sale de este default.
  mode: 'real',
  legalReady: false,
  identity: DEFAULT_IDENTITY,
  dataFiscal: { embed: '' },
  texts: {
    terminos: LEGAL_DOCS['terminos'],
    privacidad: LEGAL_DOCS['privacidad'],
    devoluciones: LEGAL_DOCS['devoluciones'],
  },
  arrepentimiento: { emailDestino: 'arrepentimiento@tutienda.com' },
  libroQuejas: { url: null },
};

/**
 * Provee el centro legal del storefront. Por default inyecta
 * {@link DEFAULT_LEGAL_CONFIG} (fail-closed: `real`/dark, no publica identidad
 * genérica). Con `demo=true` (opt-in por entorno, {@link environment.legalDemo})
 * inyecta el MISMO config pero en `mode:'demo'`: el circuito legal queda VISIBLE
 * con la identidad genérica + el banner "Datos de demostración — sin valor legal",
 * para la vitrina demostrativa. La selección la hace el ENTORNO, nunca el default.
 *
 * **Punto de integración #483:** cuando el BFF exponga la identidad legal per-org
 * por el endpoint de branding, se reemplaza este provider por un `useFactory`/
 * `useClass` que arme el MISMO {@link StorefrontLegalConfig} desde el endpoint
 * (con su `legalReady` real) — swap 1:1, sin tocar componentes ni contenido.
 * @param demo Si exponer el circuito legal en modo demo (default: `environment.legalDemo`).
 * @returns El provider del token {@link STOREFRONT_LEGAL_CONFIG}.
 */
export function provideStorefrontLegal(demo: boolean = environment.legalDemo): Provider {
  const config: StorefrontLegalConfig = demo
    ? { ...DEFAULT_LEGAL_CONFIG, mode: 'demo' }
    : DEFAULT_LEGAL_CONFIG;
  return { provide: STOREFRONT_LEGAL_CONFIG, useValue: config };
}
